<script setup lang="ts">
import type { TTeacherInfo } from "@/assets/js/globalTypes";
import router from "@/router";
import { useTeacherStore } from "@/store/teacher";
import { openConfirmDialog } from "@/components/modals/modalDefinitions";
import { $t } from "@/i18n";

defineOptions({
	name: "admin.teachers.profile",
});

definePage({
	name: "admin.teachers.profile",
	meta: {
		hasKeepAlive: ["admin.teachers.all", "admin.teachers.pending"],
		permissions: "view-teacher-profile",
		excludeInSidebar: true,
	},
});

const route = useRoute("admin.teachers.profile");

const teacherStore = useTeacherStore();

const teacher = ref<TTeacherInfo>();

onMounted(async () => {
	useTeacherStore().setTeacherValidationID(Number(route.params.id));
	teacher.value = await useTeacherStore().fetchTeacherProfile(
		Number(route.params.id),
	);
	// console.log(teacher.value);
});

function isEmpty(obj) {
	for (const prop in obj) {
		if (Object.hasOwn(obj, prop)) {
			return false;
		}
	}

	return true;
}

onDeactivated(() => {
	teacherStore.resetValidation();
});

onBeforeUnmount(() => {
	teacherStore.resetValidation();
});

const handleApproved = (id: number, type: "vrq" | "cve" | "fil") => {
	teacherStore.addApprovedFeedback(type, id);
};

const handleDisapproved = (
	id: number,
	type: "vrq" | "cve" | "fil",
	reason: string,
) => {
	teacherStore.addRejectedFeedback(type, id, reason);
};

const checkFeedbackMap = (type: "vrq" | "cve" | "fil", id: number) => {
	if (teacherStore.doesRejectedIDExist(type, id)) {
		return "disapproved";
	}
	if (teacherStore.doesApprovedIDExist(type, id)) {
		return "approved";
	}
	return "pending";
};

const handleValidation = async () => {
	// const validationPayload: TValidatePayload = {
	// 	usr_id: 0,
	// 	vrq_ids: [],
	// 	cve_ids: [],
	// 	fil_ids: [],
	// };

	// // this has to be here because ts complains if i put Number(route.params.id) directly
	// validationPayload.usr_id = Number(route.params.id);

	// if (teacher.value?.user) {
	// 	const users = teacher.value.user;

	// 	for (const key in users) {
	// 		if (users.hasOwnProperty(key)) {
	// 			validationPayload.vrq_ids.push(users[key].id);
	// 		}
	// 	}
	// }

	// // This might be a war crime, but it works
	// if (teacher.value?.experiences) {
	// 	const experiences = teacher.value.experiences;
	// 	for (const expGroupKey in experiences) {
	// 		if (experiences.hasOwnProperty(expGroupKey)) {
	// 			const expGroup = experiences[expGroupKey];
	// 			for (const exp of expGroup) {
	// 				for (const prop in exp) {
	// 					if (exp.hasOwnProperty(prop)) {
	// 						validationPayload.cve_ids.push(exp[prop].id);
	// 					}
	// 				}
	// 			}
	// 		}
	// 	}
	// }

	// if (teacher.value?.files) {
	// 	const files = teacher.value.files;
	// 	for (let i = 0, len = files.length; i < len; i++) {
	// 		validationPayload.fil_ids.push(files[i].id);
	// 	}
	// }

	// console.log(validationPayload);

	await teacherStore.validateTeacher();
	routeAfterValidation();
};

const handleDenyValidation = async () => {
	await teacherStore.denyValidation();
	routeAfterValidation();
};

const handleBlacklist = async () => {
	await teacherStore.blacklistTeacher();
	routeAfterValidation();
};

const routeAfterValidation = () => {
	router.go(-1);
};

const canValidateTeacher = computed(() => {
	const approvedCount = teacherStore.getApprovedFeedbackSize();
	const approvedIDsLength = teacherStore.getApprovedFeedbackIDs().length;

	return (
		teacherStore.isRejectMapEmpty() &&
		((!teacher.value.wasValidated && approvedCount === approvedIDsLength) ||
			(teacher.value.wasValidated && approvedCount !== approvedIDsLength) ||
			(teacher.value.wasValidated && approvedCount === approvedIDsLength))
	);
});

const getApproveText = () => {
	if (teacher.value.wasValidated) {
		return $t("app.admin.teacher.validation.dialogs.textApproveValidated");
	}
	if (
		teacher.value.wasValidated &&
		teacherStore.getApprovedFeedbackSize() !==
			teacherStore.getApprovedFeedbackIDs().length
	) {
		return $t(
			"app.admin.teacher.validation.dialogs.textApproveValidatedNotAllChecked",
		);
	}
	return $t("app.admin.teacher.validation.dialogs.textApprove");
};
</script>

<template>
	<div id="teacher-profile">
		<p-card v-if="teacher">
			<template #title>
				<span>
					{{ teacher.full_name ? teacher.full_name : "No name" }}
				</span>
			</template>
			<template #content>
				<!-- GENERAL INFORMATION START -->
				<div
					v-if="teacher && !isEmpty(teacher.user)"
					class="section-wrap"
				>
					<div class="section-wrap-title">
						<span>
							{{ $t("app.admin.teacher.validation.titles.generalInformation") }}
						</span>
					</div>
					<div class="section-wrap-content">
						<div
							v-if="teacher.user.usr_flnm || teacher.user.usr_eml"
							class="general-information-col"
						>
							<display-field
								v-if="teacher.user.usr_flnm"
								:id="teacher.user.usr_flnm.id"
								:title="$t('app.admin.teacher.validation.fields.fullName')"
								:content="teacher.user.usr_flnm.value"
								:type="'vrq'"
								can-give-feedback
								should-expand-x
								:feedback-type="
									checkFeedbackMap('vrq', teacher.user.usr_flnm.id)
								"
								@approved="handleApproved(teacher.user.usr_flnm.id, 'vrq')"
								@disapproved="
									handleDisapproved(teacher.user.usr_flnm.id, 'vrq', $event)
								"
							/>
							<display-field
								v-if="teacher.user.usr_eml"
								:id="teacher.user.usr_eml.id"
								:title="$t('app.admin.teacher.validation.fields.email')"
								:content="teacher.user.usr_eml.value"
								:type="'vrq'"
								can-give-feedback
								should-expand-x
								:feedback-type="
									checkFeedbackMap('vrq', teacher.user.usr_eml.id)
								"
								@approved="handleApproved(teacher.user.usr_eml.id, 'vrq')"
								@disapproved="
									handleDisapproved(teacher.user.usr_eml.id, 'vrq', $event)
								"
							/>
							<!-- <display-field
								title="Phone Number"
								content="+971526374859"
								can-give-feedback
								should-expand-x
							/> -->
						</div>
						<div
							v-if="teacher.user.usr_ttl"
							class="general-information-col"
						>
							<display-field
								:id="teacher.user.usr_ttl.id"
								:title="$t('app.admin.teacher.validation.fields.title')"
								:content="teacher.user.usr_ttl.value"
								:type="'vrq'"
								can-give-feedback
								should-expand-x
								:feedback-type="
									checkFeedbackMap('vrq', teacher.user.usr_ttl.id)
								"
								@approved="handleApproved(teacher.user.usr_ttl.id, 'vrq')"
								@disapproved="
									handleDisapproved(teacher.user.usr_ttl.id, 'vrq', $event)
								"
							/>
							<!-- <display-field
								title="Subjects taught"
								content="Math, Science, English"
								:can-give-feedback="false"
								should-expand-x
							/>
							<display-field
								title="Type of contract"
								content="Full-time"
								:can-give-feedback="false"
								should-expand-x
							/> -->
						</div>
						<div
							v-if="teacher.user.usr_hrrt"
							class="general-information-hourly"
						>
							<display-field
								:id="teacher.user.usr_hrrt.id"
								:title="$t('app.admin.teacher.validation.fields.hourlyRate')"
								:content="'$' + teacher.user.usr_hrrt.value"
								:type="'vrq'"
								can-give-feedback
								should-expand-x
								:feedback-type="
									checkFeedbackMap('vrq', teacher.user.usr_hrrt.id)
								"
								@approved="handleApproved(teacher.user.usr_hrrt.id, 'vrq')"
								@disapproved="
									handleDisapproved(teacher.user.usr_hrrt.id, 'vrq', $event)
								"
							/>
						</div>
						<div class="general-information-desc">
							<!-- <display-field
								title="Description"
								content="Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos."
								can-give-feedback
								should-expand-x
								should-expand-y
							/> -->
						</div>
					</div>
				</div>
				<!-- GENERAL INFORMATION END -->
				<hr v-if="teacher && !isEmpty(teacher.user)" />
				<!-- GENERAL INFORMATION PART 2 START -->
				<div
					v-if="teacher.user.usr_avtr"
					class="general-information-part-2"
				>
					<div class="general-information-part-2-content">
						<picture-display
							:id="teacher.user.usr_avtr.id"
							:title="$t('app.admin.teacher.validation.fields.profilePicture')"
							:image-url="teacher.user.usr_avtr.value"
							can-give-feedback
							:feedback-type="checkFeedbackMap('vrq', teacher.user.usr_avtr.id)"
							@approved="handleApproved(teacher.user.usr_avtr.id, 'vrq')"
							@disapproved="
								handleDisapproved(teacher.user.usr_avtr.id, 'vrq', $event)
							"
						/>
					</div>
				</div>
				<!-- GENERAL INFORMATION PART 2 END -->
				<hr v-if="teacher.user.usr_avtr" />
				<!-- EDUCATION START -->
				<div
					v-if="teacher.experiences.edu.length > 0"
					class="section-wrap"
				>
					<div class="section-wrap-title">
						<span>
							{{ $t("app.admin.teacher.validation.titles.education") }}
						</span>
					</div>
					<div class="section-wrap-content">
						<div
							v-for="(edu, index) of teacher.experiences.edu"
							:key="index"
							class="third-section-item"
						>
							<teacher-education-display :data="edu" />
						</div>
						<!-- <div class="third-section-item">
							<teacher-education-display />
						</div>
						<div class="third-section-item">
							<teacher-education-display />
						</div> -->
					</div>
				</div>
				<!-- EDUCATION END -->
				<hr v-if="teacher.experiences.edu.length > 0" />
				<!-- PROFESSIONAL EXPERIENCE START -->
				<div
					v-if="teacher.experiences.pexp.length > 0"
					class="section-wrap"
				>
					<div class="section-wrap-title">
						<span>
							{{
								$t("app.admin.teacher.validation.titles.professionalExperience")
							}}
						</span>
					</div>
					<div class="section-wrap-content">
						<div
							v-for="(pexp, index) of teacher.experiences.pexp"
							:key="index"
							class="half-section-item"
						>
							<teacher-professional-exp-display :data="pexp" />
						</div>
						<!-- <div class="half-section-item">
							<teacher-professional-exp-display />
						</div> -->
					</div>
				</div>
				<!-- PROFESSIONAL EXPERIENCE END -->
				<hr v-if="teacher.experiences.pexp.length > 0" />
				<!-- EXPERIENCE IN EDUCATION START -->
				<div
					v-if="teacher.experiences.texp.length > 0"
					class="section-wrap"
				>
					<div class="section-wrap-title">
						<span>
							{{
								$t("app.admin.teacher.validation.titles.experienceInEducation")
							}}
						</span>
					</div>
					<div class="section-wrap-content">
						<div
							v-for="(texp, index) of teacher.experiences.texp"
							:key="index"
							class="half-section-item"
						>
							<teacher-edu-exp-display :data="texp" />
						</div>
						<!-- <div class="half-section-item">
							<teacher-edu-exp-display />
						</div> -->
					</div>
				</div>
				<!-- EXPERIENCE IN EDUCATION END -->
				<hr v-if="teacher.experiences.texp.length > 0" />
				<!-- CERTIFICATES START -->
				<div
					v-if="teacher.files.length > 0"
					class="section-wrap"
				>
					<div class="section-wrap-title">
						<span>
							{{ $t("app.admin.teacher.validation.titles.certificates") }}
						</span>
					</div>
					<div class="section-wrap-content flex-wrap">
						<file-display
							v-for="file of teacher.files"
							:id="file.id"
							:key="file.id"
							:title="file.title"
							:file-urls="file.links"
							can-give-feedback
							@approved="handleApproved(file.id, 'fil')"
							@disapproved="handleDisapproved(file.id, 'fil', $event)"
						/>
						<!-- <file-display
							title="Certificate 2"
							:file-urls="['https://picsum.photos/150']"
							can-give-feedback
						/>
						<file-display
							title="Certificate 3"
							:file-urls="['https://picsum.photos/150']"
							can-give-feedback
						/> -->
					</div>
				</div>
				<!-- CERTIFICATES END -->
				<!-- BUTTONS START -->
				<div class="button-wrap">
					<p-button
						v-if="canValidateTeacher"
						severity="success"
						@click="
							openConfirmDialog(
								$t('app.admin.teacher.validation.dialogs.titleApprove'),
								getApproveText(),
								handleValidation,
							)
						"
					>
						{{ $t("app.admin.teacher.validation.buttons.approve") }}
					</p-button>
					<p-button
						severity="danger"
						outlined
						:disabled="teacherStore.isRejectMapEmpty()"
						@click="
							openConfirmDialog(
								$t('app.admin.teacher.validation.dialogs.titleDisapprove'),
								$t('app.admin.teacher.validation.dialogs.textDisapprove'),
								handleDenyValidation,
							)
						"
					>
						{{ $t("app.admin.teacher.validation.buttons.disapprove") }}
					</p-button>
					<p-button
						severity="danger"
						@click="
							openConfirmDialog(
								$t('app.admin.teacher.validation.dialogs.titleBlacklist'),
								$t('app.admin.teacher.validation.dialogs.textBlacklist'),
								handleBlacklist,
							)
						"
					>
						{{ $t("app.admin.teacher.validation.buttons.blacklist") }}
					</p-button>
				</div>
				<!-- BUTTONS END -->
			</template>
		</p-card>
	</div>
</template>

<style scoped lang="scss">
$title-color: #292929;

hr {
	margin: 60px 0;
}
.section-wrap {
	display: flex;
	flex-direction: column;
	gap: 20px;
	width: 100%;

	.section-wrap-title {
		font-size: 18px;
		color: $title-color;
		font-weight: 600;
		font-family: Roboto, sans-serif;
	}

	.section-wrap-content {
		width: 100%;
		display: flex;
		gap: 50px 20px;
		// column-gap: 20px;
		// row-gap: 40px;
		flex-wrap: wrap;

		.general-information-col {
			display: flex;
			flex-direction: column;
			gap: 20px;
			width: 25%;
		}

		.general-information-hourly {
			width: 12%;
		}

		.third-section-item {
			width: 32%;
		}

		.half-section-item {
			width: calc(50% - 10px);
		}
	}
}

.general-information-part-2 {
	.general-information-part-2-content {
		padding-left: 50px;
	}
}

.button-wrap {
	display: flex;
	gap: 20px;
	justify-content: center;
	margin-top: 60px;
}
</style>
